import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getRiskTrends, getIncidentTrends, getCAPATrends } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Minus, Activity, 
  RefreshCw, BarChart3, LineChart as LineChartIcon, Target,
  Clock, CheckCircle2, XCircle, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { cn } from '@/lib/utils';

const RISK_COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#eab308',
  low: '#22c55e'
};

const TREND_PERIODS = [
  { value: '6', label: '6 Months' },
  { value: '12', label: '12 Months' },
  { value: '24', label: '24 Months' }
];

export default function TrendsPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('12');
  const [activeTab, setActiveTab] = useState('risks');
  
  const [riskTrends, setRiskTrends] = useState(null);
  const [incidentTrends, setIncidentTrends] = useState(null);
  const [capaTrends, setCapaTrends] = useState(null);

  useEffect(() => {
    if (api) {
      fetchAllTrends();
    }
  }, [api, period]);

  const fetchAllTrends = async () => {
    try {
      setLoading(true);
      const months = parseInt(period);
      const [riskRes, incidentRes, capaRes] = await Promise.all([
        getRiskTrends(api, months),
        getIncidentTrends(api, months),
        getCAPATrends(api, months)
      ]);
      setRiskTrends(riskRes.data);
      setIncidentTrends(incidentRes.data);
      setCapaTrends(capaRes.data);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllTrends();
    setRefreshing(false);
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'increasing': return <TrendingUp className="w-5 h-5 text-red-400" />;
      case 'decreasing': return <TrendingDown className="w-5 h-5 text-emerald-400" />;
      default: return <Minus className="w-5 h-5 text-slate-400" />;
    }
  };

  const getTrendBadge = (direction) => {
    switch (direction) {
      case 'increasing': 
        return <Badge className="bg-red-600/30 text-red-300 border-red-500/50">Increasing Risk</Badge>;
      case 'decreasing': 
        return <Badge className="bg-emerald-600/30 text-emerald-300 border-emerald-500/50">Improving</Badge>;
      default: 
        return <Badge className="bg-slate-600/30 text-slate-300 border-slate-500/50">Stable</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="trends-title">
            Safety Trends Analysis
          </h1>
          <p className="text-slate-400 mt-1">Historical trends for risks, incidents, and CAPAs</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700" data-testid="period-select">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {TREND_PERIODS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="refresh-trends-btn"
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Risk Score Card */}
        <Card className="bg-slate-900/60 border-slate-800" data-testid="risk-score-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Current Risk Score</p>
                <p className="text-4xl font-bold text-white mt-1">{riskTrends?.current_risk_score || 0}</p>
                <div className="flex items-center gap-2 mt-2">
                  {getTrendIcon(riskTrends?.trend_direction)}
                  {getTrendBadge(riskTrends?.trend_direction)}
                </div>
              </div>
              <div className="w-14 h-14 rounded-xl bg-orange-600/20 flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Incidents Card */}
        <Card className="bg-slate-900/60 border-slate-800" data-testid="incidents-summary-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Incidents ({period}M)</p>
                <p className="text-4xl font-bold text-white mt-1">{incidentTrends?.total_incidents || 0}</p>
                <p className="text-slate-500 text-sm mt-2">
                  Avg: {(incidentTrends?.avg_monthly || 0).toFixed(1)}/month
                </p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-red-600/20 flex items-center justify-center">
                <Activity className="w-7 h-7 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CAPA Closure Rate Card */}
        <Card className="bg-slate-900/60 border-slate-800" data-testid="capa-closure-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg CAPA Closure Rate</p>
                <p className="text-4xl font-bold text-emerald-400 mt-1">{capaTrends?.avg_closure_rate || 0}%</p>
                <p className="text-slate-500 text-sm mt-2">
                  {capaTrends?.total_closed || 0} of {capaTrends?.total_created || 0} closed
                </p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-emerald-600/20 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend Charts */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-900/60 border border-slate-800 p-1">
          <TabsTrigger 
            value="risks" 
            className="data-[state=active]:bg-slate-700 data-[state=active]:text-white"
            data-testid="tab-risks"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Risk Trends
          </TabsTrigger>
          <TabsTrigger 
            value="incidents"
            className="data-[state=active]:bg-slate-700 data-[state=active]:text-white"
            data-testid="tab-incidents"
          >
            <Activity className="w-4 h-4 mr-2" />
            Incident Trends
          </TabsTrigger>
          <TabsTrigger 
            value="capas"
            className="data-[state=active]:bg-slate-700 data-[state=active]:text-white"
            data-testid="tab-capas"
          >
            <Target className="w-4 h-4 mr-2" />
            CAPA Trends
          </TabsTrigger>
        </TabsList>

        {/* Risk Trends Tab */}
        <TabsContent value="risks" className="mt-6 space-y-6">
          {/* Risk Level Stacked Area Chart */}
          <Card className="bg-slate-900/60 border-slate-800" data-testid="risk-level-chart">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-400" />
                Risk Levels Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={riskTrends?.months || []}>
                    <defs>
                      <linearGradient id="colorCritical" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={RISK_COLORS.critical} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={RISK_COLORS.critical} stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorHigh" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={RISK_COLORS.high} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={RISK_COLORS.high} stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorMedium" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={RISK_COLORS.medium} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={RISK_COLORS.medium} stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorLow" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={RISK_COLORS.low} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={RISK_COLORS.low} stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month_label" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="critical" name="Critical" stackId="1" stroke={RISK_COLORS.critical} fill="url(#colorCritical)" />
                    <Area type="monotone" dataKey="high" name="High" stackId="1" stroke={RISK_COLORS.high} fill="url(#colorHigh)" />
                    <Area type="monotone" dataKey="medium" name="Medium" stackId="1" stroke={RISK_COLORS.medium} fill="url(#colorMedium)" />
                    <Area type="monotone" dataKey="low" name="Low" stackId="1" stroke={RISK_COLORS.low} fill="url(#colorLow)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Risk Score Trend */}
          <Card className="bg-slate-900/60 border-slate-800" data-testid="risk-score-chart">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <LineChartIcon className="w-5 h-5 text-sky-400" />
                Risk Score Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={riskTrends?.months || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month_label" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="risk_score" 
                      name="Risk Score"
                      stroke="#0ea5e9" 
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#0ea5e9' }}
                      activeDot={{ r: 6, fill: '#0ea5e9' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incident Trends Tab */}
        <TabsContent value="incidents" className="mt-6 space-y-6">
          <Card className="bg-slate-900/60 border-slate-800" data-testid="incident-trend-chart">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-red-400" />
                Incidents Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={incidentTrends?.months || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month_label" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                    />
                    <Legend />
                    <Bar dataKey="total" name="Total Incidents" fill="#ef4444" opacity={0.8} />
                    <Line type="monotone" dataKey="closed" name="Closed" stroke="#22c55e" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Severity Breakdown */}
          <Card className="bg-slate-900/60 border-slate-800" data-testid="severity-breakdown-chart">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Incidents by Severity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={incidentTrends?.months || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month_label" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                    />
                    <Legend />
                    <Bar dataKey="catastrophic" name="Catastrophic" stackId="a" fill="#dc2626" />
                    <Bar dataKey="hazardous" name="Hazardous" stackId="a" fill="#ea580c" />
                    <Bar dataKey="major" name="Major" stackId="a" fill="#f59e0b" />
                    <Bar dataKey="minor" name="Minor" stackId="a" fill="#22c55e" />
                    <Bar dataKey="negligible" name="Negligible" stackId="a" fill="#64748b" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CAPA Trends Tab */}
        <TabsContent value="capas" className="mt-6 space-y-6">
          <Card className="bg-slate-900/60 border-slate-800" data-testid="capa-trend-chart">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-400" />
                CAPA Creation vs Closure
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={capaTrends?.months || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month_label" stroke="#64748b" fontSize={12} />
                    <YAxis yAxisId="left" stroke="#64748b" fontSize={12} />
                    <YAxis yAxisId="right" orientation="right" stroke="#64748b" fontSize={12} unit="%" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="created" name="Created" fill="#f59e0b" opacity={0.8} />
                    <Bar yAxisId="left" dataKey="closed" name="Closed" fill="#22c55e" opacity={0.8} />
                    <Line yAxisId="right" type="monotone" dataKey="closure_rate" name="Closure Rate (%)" stroke="#0ea5e9" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
